import axios from 'helpers/axios';
import { useUser } from 'hooks';
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
  TFALogin,
  TFAReset,
  TFASetup,
  TFASMS,
  TFASMSVerify,
  TFAStart,
  TFASuccess,
} from 'ui/components/pages/2fa';
import {
  Companies as AdminCompanies,
  Dashboard as AdminDashboard,
  Logs,
  Reports,
  Users as UsersManagement,
} from 'ui/components/pages/admin';
import Branding from 'ui/components/pages/admin/Branding';
import Notifications from 'ui/components/pages/admin/Notifications';
import {
  ForgotPassword,
  PhoneConfirm,
  PhoneValidate,
  ResetPassword,
  ResetPasswordSuccess,
  SignIn,
  SignUp,
  SignUpSuccess,
} from 'ui/components/pages/auth';
import {
  About,
  Companies,
  Dashboard,
  EditJob,
  Feedback,
  Orphans,
  Settings,
} from 'ui/components/pages/user';

export default function App() {
  const { isLoggedIn, fetchUser } = useUser();
  const [branding, setBranding] = React.useState({});

  const getBranding = React.useCallback(async () => {
    try {
      const { data } = await axios.get('/branding');
      if (data) {
        setBranding(data);
      } else {
        setBranding({ title: 'Process watch' });
      }
    } catch {
      setBranding({ title: 'Process watch' });
    }
  }, []);

  React.useEffect(() => {
    getBranding();
  }, []);

  React.useEffect(() => {
    if (branding && branding.title) {
      document.title = branding.title;
    }
  }, [branding]);

  const auth = React.useCallback(() => {
    fetchUser();
  }, [fetchUser]);

  React.useEffect(() => {
    if (isLoggedIn) return;
    auth();
  }, [isLoggedIn]);

  React.useEffect(() => {});
  return (
    <Switch>
      <Route path="/dashboard" exact>
        <Dashboard />
      </Route>
      <Route path="/settings" exact>
        <Settings />
      </Route>
      <Route path="/feedback" exact>
        <Feedback />
      </Route>
      <Route path="/companies" exact>
        <Companies />
      </Route>
      <Route path="/about" exact>
        <About />
      </Route>
      <Route path="/orphans" exact>
        <Orphans />
      </Route>
      <Route path="/sign-in">
        <SignIn />
      </Route>
      <Route path="/forgot-password">
        <ForgotPassword />
      </Route>
      <Route
        path="/sign-up"
        render={({ match: { url } }) => (
          <Switch>
            <Route path={`${url}/`} exact>
              <SignUp />
            </Route>
            <Route path={`${url}/success`}>
              <SignUpSuccess />
            </Route>
            <Redirect to="/sign-in" />
          </Switch>
        )}
      />
      <Route
        path="/2fa"
        render={({ match: { url } }) => (
          <Switch>
            <Route path={`${url}/login`}>
              <TFALogin />
            </Route>
            <Route path={`${url}/get-started`}>
              <TFAStart />
            </Route>
            <Route path={`${url}/setup`}>
              <TFASetup />
            </Route>
            <Route path={`${url}/reset`}>
              <TFAReset />
            </Route>
            <Route path={`${url}/success`}>
              <TFASuccess />
            </Route>
            <Route exact path={`${url}/sms`}>
              <TFASMS />
            </Route>
            <Route path={`${url}/sms/verify`}>
              <TFASMSVerify />
            </Route>
            <Redirect to="/2fa/login" />
          </Switch>
        )}
      />
      <Route
        path="/reset-password"
        render={({ match: { url } }) => (
          <Switch>
            <Route path={`${url}/success`}>
              <ResetPasswordSuccess />
            </Route>
            <Route path={`${url}/:token`}>
              <ResetPassword />
            </Route>
            <Redirect to="/sign-in" />
          </Switch>
        )}
      />
      <Route
        path="/phone"
        render={({ match: { url } }) => (
          <Switch>
            <Route path={`${url}/validate`}>
              <PhoneValidate />
            </Route>
            <Route path={`${url}/confirm`}>
              <PhoneConfirm />
            </Route>
            <Redirect to="/phone/validate" />
          </Switch>
        )}
      />
      <Route
        path="/admin"
        render={({ match: { url } }) => (
          <Switch>
            <Route path={`${url}/dashboard`} exact>
              <AdminDashboard />
            </Route>
            <Route path={`${url}/companies`} exact>
              <AdminCompanies />
            </Route>
            <Route path={`${url}/users`} exact>
              <UsersManagement />
            </Route>
            <Route path={`${url}/reports`} exact>
              <Reports />
            </Route>
            <Route path={`${url}/logs`} exact>
              <Logs />
            </Route>
            <Route path={`${url}/notifications`} exact>
              <Notifications />
            </Route>
            <Route path={`${url}/branding`} exact>
              <Branding />
            </Route>
            <Redirect to={`${url}/dashboard`} />
          </Switch>
        )}
      />
      <Route
        path="/jobs/:id"
        render={({ match: { path } }) => (
          <Switch>
            <Route path={`${path}/edit`} exact>
              <EditJob />
            </Route>
            <Redirect to={`${path}/edit`} />
          </Switch>
        )}
      />
      {/* anything else */}
      <RedirectToRoot />
    </Switch>
  );
}

const RedirectToRoot = () => {
  const { isLoggedIn, redirectToDashboard } = useUser();

  React.useEffect(() => {
    if (isLoggedIn) {
      redirectToDashboard();
    }
  }, [isLoggedIn]);

  return null;
};
